import { useEffect, useRef, useState } from "react";
import { cancelJob, subscribeJobStream } from "@/api/jobs";
import { useAccessToken } from "@/auth/useAccessToken";
import { CopyWithSourcesButton } from "@/components/citations/CopyWithSourcesButton";
import { MessageRenderer } from "@/components/thread/MessageRenderer";
import { citationLocation } from "@/lib/citationLocation";
import { hasCopyableSources } from "@/lib/copyWithSources";
import { isTopModal, useModalStack } from "@/lib/modalStack";
import { useExitTransition } from "@/lib/useExitTransition";
import { useFocusTrap } from "@/lib/useFocusTrap";
import { normalizeCitations } from "@/runtime/citationDiscriminator";
import type { CitationRef, JobSnapshot, JobStatus, StreamEvent } from "@/types";

const MODAL_ID = "job-detail-drawer";

interface Props {
  job: JobSnapshot | null;
  tenantId: string | null;
  onClose: () => void;
}

const ACTIVE_STATUSES: ReadonlySet<JobStatus> = new Set([
  "queued",
  "running",
  "planning",
  "retrieving",
  "synthesizing",
  "verifying",
]);

function isActive(s: JobStatus): boolean {
  return ACTIVE_STATUSES.has(s);
}

function statusBadgeClass(status: JobStatus): string {
  if (status === "completed") return "bg-[var(--status-success-bg)] text-[var(--status-success)]";
  if (status === "failed" || status === "dead_lettered")
    return "bg-[var(--status-error-bg)] text-[var(--status-error)]";
  if (status === "cancelled") return "bg-[var(--surface-sunken)] text-[var(--text-secondary)]";
  return "bg-[var(--accent-soft)] text-[var(--accent-text)]";
}

interface DrawerCitation {
  index: number;
  doc_id: string;
  chunk_id: string;
  page_idx?: number | null;
  preview?: string;
  doc_title?: string | null;
  sheet_name?: string | null;
  cell_range?: string | null;
}

// ADR-096 Phase 3 — delegate defensive narrowing to normalizeCitations
// (the canonical chokepoint accepting BOTH legacy ADR-082 and new
// ADR-096 cite shapes). This drawer's local DrawerCitation slim shape
// is preserved for rendering simplicity — the drawer only consumes 6
// of CitationRef's ~15 fields. iter 19 mirror invariant (cf.
// citationNarrowing.ts) preserved: both consumers now route through
// the same normalizeCitations delegation, so cohort awareness is
// gained for free without re-implementing the per-cite shape check.
function toDrawerCitations(raw: unknown): DrawerCitation[] {
  return normalizeCitations(raw).map((c) => ({
    index: c.index,
    doc_id: c.doc_id,
    chunk_id: c.chunk_id,
    page_idx: typeof c.page_idx === "number" ? c.page_idx : null,
    preview: typeof c.preview === "string" ? c.preview : undefined,
    doc_title: typeof c.doc_title === "string" ? c.doc_title : null,
    sheet_name: typeof c.sheet_name === "string" ? c.sheet_name : null,
    cell_range: typeof c.cell_range === "string" ? c.cell_range : null,
  }));
}

function durationMs(job: JobSnapshot): number | null {
  if (!job.started_at || !job.completed_at) return null;
  const ms = Date.parse(job.completed_at) - Date.parse(job.started_at);
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
}

export function JobDetailDrawer({ job, tenantId, onClose }: Props) {
  const { acquireToken } = useAccessToken();
  const [liveAnswer, setLiveAnswer] = useState("");
  const [liveCitations, setLiveCitations] = useState<CitationRef[]>([]);
  const [cancelling, setCancelling] = useState(false);
  const dialogRef = useRef<HTMLElement | null>(null);
  const isOpen = job !== null;
  const { rendered, closing } = useExitTransition(isOpen);
  // Retain the last non-null job so the drawer can finish its exit
  // animation after `job` flips null (DESIGN.md §6 — animate IN and OUT).
  const lastJobRef = useRef<JobSnapshot | null>(job);
  if (job) lastJobRef.current = job;
  const shownJob = job ?? lastJobRef.current;
  useModalStack(MODAL_ID, isOpen);
  useFocusTrap(dialogRef, isOpen);

  useEffect(() => {
    setLiveAnswer("");
    setLiveCitations([]);
    setCancelling(false);
  }, [job?.job_id]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && isTopModal(MODAL_ID)) onClose();
    }
    if (job) {
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
    return undefined;
  }, [job, onClose]);

  useEffect(() => {
    if (!job || !tenantId || !isActive(job.status)) return undefined;
    const controller = new AbortController();
    let aborted = false;

    (async () => {
      try {
        const token = await acquireToken();
        await subscribeJobStream(
          tenantId,
          job.job_id,
          token,
          {
            onEvent: (event: StreamEvent) => {
              if (aborted) return;
              if (event.type === "token") {
                setLiveAnswer((prev) => prev + event.delta);
              } else if (event.type === "citation") {
                setLiveCitations((prev) => [...prev, event.citation]);
              }
            },
            onError: (err) => {
              // eslint-disable-next-line no-console
              console.warn("[ADR-091 drawer] subscribe error", err);
            },
          },
          controller.signal,
          0,
        );
      } catch (err) {
        if (!aborted) {
          // eslint-disable-next-line no-console
          console.warn("[ADR-091 drawer] startup error", err);
        }
      }
    })();

    return () => {
      aborted = true;
      controller.abort();
    };
  }, [job?.job_id, job?.status, tenantId, acquireToken]);

  if (!rendered || !shownJob) return null;

  const active = isActive(shownJob.status);
  const displayedAnswer = active ? liveAnswer : (shownJob.answer_text ?? "");
  const rawCitations: unknown[] = active
    ? (liveCitations as unknown[])
    : Array.isArray(shownJob.citations_jsonb)
      ? shownJob.citations_jsonb
      : [];
  const citations = toDrawerCitations(rawCitations);
  // MessageRenderer classifies cohort from the FULL CitationRef
  // (answer_span_* / marker_index); the slim `citations` above would
  // misclassify P-Cite as legacy and drop every inline attribution chip.
  const rendererCitations = normalizeCitations(rawCitations);
  const dur = durationMs(shownJob);

  async function handleCancel() {
    if (!tenantId || cancelling || !job) return;
    setCancelling(true);
    try {
      const token = await acquireToken();
      await cancelJob(tenantId, job.job_id, token);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[ADR-091 drawer] cancel failed", err);
    }
    onClose();
  }

  return (
    <>
      <div
        className={`${closing ? "bk-anim-backdrop-out" : "bk-anim-backdrop"} fixed inset-0 z-40 bg-[var(--scrim)]`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        ref={dialogRef}
        role="dialog"
        aria-label="Job detail"
        className={`${closing ? "bk-anim-drawer-out" : "bk-anim-drawer"} fixed right-0 top-0 z-50 flex h-full w-full flex-col border-l border-[var(--border-default)] bg-[var(--surface-raised)] shadow-[var(--elev-whisper-xl)] sm:max-w-md`}
      >
        <header className="flex items-center gap-2 border-b border-[var(--border-default)] px-4 py-3">
          <span
            className={`type-caption rounded-[var(--radius-pill)] px-2 py-0.5 font-medium ${statusBadgeClass(shownJob.status)}`}
          >
            {shownJob.status}
          </span>
          <span className="type-caption font-mono text-[var(--text-tertiary)]">
            {shownJob.job_id.slice(0, 8)}
          </span>
          {active && (
            <span className="type-caption flex items-center gap-1 text-[var(--text-tertiary)]">
              <span
                aria-hidden="true"
                className="inline-block h-2 w-2 animate-pulse rounded-full bg-[var(--accent-primary)]"
              />
              Live
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto rounded-[var(--radius-pill)] p-1.5 text-[var(--text-secondary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)]"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          {shownJob.query_text && (
            <section>
              <div className="type-overline text-[var(--text-tertiary)]">Query</div>
              <div className="type-body mt-1 text-[var(--text-primary)]">{shownJob.query_text}</div>
            </section>
          )}

          {displayedAnswer && (
            <section>
              <div className="flex items-center justify-between">
                <div className="type-overline text-[var(--text-tertiary)]">Answer</div>
                {!active && hasCopyableSources(rendererCitations) && (
                  <CopyWithSourcesButton
                    content={displayedAnswer}
                    citations={rendererCitations}
                    className="type-caption rounded-[var(--radius-pill)] px-2.5 py-0.5 text-[var(--text-secondary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)]"
                  />
                )}
              </div>
              <div className="mt-1 text-[var(--text-primary)]">
                <MessageRenderer content={displayedAnswer} citations={rendererCitations} />
              </div>
            </section>
          )}
          {active && !displayedAnswer && (
            <div className="type-caption italic text-[var(--text-tertiary)]">
              Waiting for first token…
            </div>
          )}

          {citations.length > 0 && (
            <section>
              <div className="type-overline text-[var(--text-tertiary)]">
                Citations ({citations.length})
              </div>
              <ol className="mt-1 space-y-2">
                {citations.map((c) => (
                  <li
                    key={`${c.index}-${c.chunk_id}`}
                    className="rounded-[var(--radius-md)] bg-[var(--citation-bg)] p-2 ring-1 ring-[var(--accent-ring)]"
                  >
                    <div className="type-caption flex items-baseline gap-1.5">
                      <span className="font-semibold text-[var(--text-secondary)]">[{c.index}]</span>
                      <span className="truncate font-medium text-[var(--text-primary)]">
                        {c.doc_title ?? c.doc_id}
                      </span>
                      {citationLocation(c) !== null && (
                        <span className="ml-auto flex-shrink-0 text-[var(--text-tertiary)]">
                          {citationLocation(c)?.label}
                        </span>
                      )}
                    </div>
                    {c.doc_title && (
                      <div className="type-caption mt-0.5 truncate font-mono text-[var(--text-tertiary)]">
                        {c.doc_id}
                      </div>
                    )}
                    {c.preview && (
                      <div className="type-body-sm mt-1 text-[var(--text-primary)]">{c.preview}</div>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {shownJob.terminated_reason && (
            <section>
              <div className="type-overline text-[var(--text-tertiary)]">Terminated reason</div>
              <div className="type-caption mt-1 font-mono text-[var(--text-primary)]">
                {shownJob.terminated_reason}
              </div>
            </section>
          )}

          {shownJob.error_detail && (
            <section>
              <div className="type-overline text-[var(--status-error)]">Error</div>
              <div className="type-caption mt-1 whitespace-pre-wrap font-mono text-[var(--text-primary)]">
                {shownJob.error_detail}
              </div>
            </section>
          )}

          <section className="grid grid-cols-2 gap-2 border-t border-[var(--border-default)] pt-3">
            {shownJob.cost_usd_total > 0 && (
              <div>
                <div className="type-overline text-[var(--text-tertiary)]">Cost</div>
                <div className="type-body-sm mt-0.5 text-[var(--text-primary)]">
                  ${shownJob.cost_usd_total.toFixed(4)}
                </div>
              </div>
            )}
            {dur !== null && (
              <div>
                <div className="type-overline text-[var(--text-tertiary)]">Duration</div>
                <div className="type-body-sm mt-0.5 text-[var(--text-primary)]">
                  {dur < 1000 ? `${dur}ms` : `${(dur / 1000).toFixed(1)}s`}
                </div>
              </div>
            )}
          </section>
        </div>
        {active && (
          <footer className="border-t border-[var(--border-default)] px-4 py-3">
            <button
              type="button"
              onClick={() => void handleCancel()}
              disabled={cancelling}
              className="type-body-sm w-full rounded-[var(--radius-pill)] bg-[var(--status-error)] px-4 py-2 font-medium text-[var(--text-on-accent)] transition-opacity duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:opacity-90 focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)] disabled:opacity-50"
            >
              {cancelling ? "Cancelling…" : "Cancel job"}
            </button>
          </footer>
        )}
      </aside>
    </>
  );
}
